import React, { Children, cloneElement, Fragment, useRef } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import Avatar from "./avatar"
import useMergedRef from "design_system/core/hooks/use_merge_ref"
import "./index.css"
const GroupContainer = React.forwardRef(
  ({ children, chained, className }, ref) => (
    <div
      ref={ref}
      className={classNames(
        "avatar-group",
        chained && "avatar-group-chained",
        className
      )}
    >
      {children}
    </div>
  )
)

GroupContainer.displayName = "AvatarGroupContainer"

const AvatarGroup = React.forwardRef(
  (
    {
      maxCount = 3,
      chained = true,
      className,
      omittedAvatarTooltip,
      onOmittedAvatarClick,
      omittedAvatarProps,
      omittedAvatarContent,
      children,
    },
    ref
  ) => {
    const childCount = Children.count(children)
    const avatarNode = useRef()
    const avatarMergeRef = useMergedRef(ref, avatarNode)

    const childWithKey = Children.toArray(children).map((child, index) =>
      cloneElement(child, {
        key: `grouped-avatar-${index}`,
      })
    )

    if (maxCount && maxCount < childCount) {
      const childToShow = childWithKey.slice(0, maxCount)
      const overflowCount = childCount - maxCount
      const avatar = (
        <Avatar
          className={onOmittedAvatarClick ? "cursor-pointer" : ""}
          onClick={() => onOmittedAvatarClick?.()}
          tooltipText={omittedAvatarTooltip && `${overflowCount} More`}
          {...omittedAvatarProps}
        >
          {omittedAvatarContent || `+${overflowCount}`}
        </Avatar>
      )

      childToShow.push(<Fragment key="avatar-more-tooltip">{avatar}</Fragment>)
      return (
        <GroupContainer
          ref={avatarMergeRef}
          className={className}
          chained={chained}
        >
          {childToShow}
        </GroupContainer>
      )
    }

    return (
      <GroupContainer
        ref={avatarMergeRef}
        className={className}
        chained={chained}
      >
        {children}
      </GroupContainer>
    )
  }
)

AvatarGroup.displayName = "AvatarGroup"

AvatarGroup.propTypes = {
  chained: PropTypes.bool,
  /**
   * Set this to `0` to show all items inside this group
   */
  maxCount: PropTypes.number,
  omittedAvatarContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  omittedAvatarProps: PropTypes.object,
  omittedAvatarTooltip: PropTypes.bool,
  onOmittedAvatarClick: PropTypes.func,
}

export default AvatarGroup
